import React, {useCallback, useEffect, useRef, useState} from "react";
import {useLoadScript} from "@react-google-maps/api";

// ===== MERCATOR PROJECTION HELPERS =====

const EARTH_RADIUS = 6378137; // meters
const MAX_MERCATOR_LAT = 85.05112878; // Web Mercator limit

// Convert lat/lng to Mercator coordinates (x, y in meters)
function latLngToMercator(lat: number, lng: number): {x: number; y: number} {
    const clampedLat = Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, lat));
    const x = (lng * Math.PI / 180) * EARTH_RADIUS;
    const y = Math.log(Math.tan((90 + clampedLat) * Math.PI / 360)) * EARTH_RADIUS;
    return {x, y};
}

// Convert Mercator coordinates to screen pixels
function mercatorToScreen(
    mercatorX: number,
    mercatorY: number,
    mapCenter: {lat: number; lng: number},
    zoom: number,
    canvasWidth: number,
    canvasHeight: number
): {x: number; y: number} | null {
    const centerMercator = latLngToMercator(mapCenter.lat, mapCenter.lng);

    // Mercator scale at given zoom level
    // At zoom 0, the entire world (256 pixels wide) = 2 * PI * EARTH_RADIUS meters
    const scale = (256 / (2 * Math.PI * EARTH_RADIUS)) * Math.pow(2, zoom);

    // Offset from center in pixels
    const dx = (mercatorX - centerMercator.x) * scale;
    const dy = (centerMercator.y - mercatorY) * scale; // Y flipped (Mercator Y increases northward, screen Y increases downward)

    // Screen coordinates (origin at center of canvas)
    const screenX = canvasWidth / 2 + dx;
    const screenY = canvasHeight / 2 + dy;

    return {x: screenX, y: screenY};
}

// Convert screen pixels to NDC (normalized device coordinates for WebGL)
function screenToNDC(screenX: number, screenY: number, canvasWidth: number, canvasHeight: number): {x: number; y: number} {
    return {
        x: (screenX / canvasWidth) * 2 - 1,
        y: -((screenY / canvasHeight) * 2 - 1)
    };
}

// Combined helper: lat/lng → screen → NDC
function latLngToNDC(
    lat: number,
    lng: number,
    mapCenter: {lat: number; lng: number},
    zoom: number,
    canvasWidth: number,
    canvasHeight: number
): {x: number; y: number} | null {
    const mercator = latLngToMercator(lat, lng);
    const screen = mercatorToScreen(mercator.x, mercator.y, mapCenter, zoom, canvasWidth, canvasHeight);
    if (!screen) return null;
    return screenToNDC(screen.x, screen.y, canvasWidth, canvasHeight);
}

// Calculate destination point using haversine
function destinationPoint(lat: number, lng: number, bearing: number, distanceMeters: number): {lat: number; lng: number} {
    const toRad = (deg: number) => deg * Math.PI / 180;
    const toDeg = (rad: number) => rad * 180 / Math.PI;

    const latRad = toRad(lat);
    const lngRad = toRad(lng);
    const bearingRad = toRad(bearing);

    const angularDistance = distanceMeters / EARTH_RADIUS;

    const lat2Rad = Math.asin(
        Math.sin(latRad) * Math.cos(angularDistance) +
        Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(bearingRad)
    );

    const lng2Rad = lngRad + Math.atan2(
        Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(latRad),
        Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(lat2Rad)
    );

    let lat2 = toDeg(lat2Rad);
    let lng2 = toDeg(lng2Rad);

    // Clamp to Mercator bounds
    if (lat2 > MAX_MERCATOR_LAT) lat2 = MAX_MERCATOR_LAT;
    if (lat2 < -MAX_MERCATOR_LAT) lat2 = -MAX_MERCATOR_LAT;

    // Normalize longitude
    while (lng2 > 180) lng2 -= 360;
    while (lng2 < -180) lng2 += 360;

    return {lat: lat2, lng: lng2};
}

// Haversine distance
function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const toRad = (deg: number) => deg * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS * c / 1000; // km
}

// ===== OVERLAY CLASS =====

function createOverlay(drawFunc: () => void) {
    class SimpleOverlay extends google.maps.OverlayView {
        private isReady = false;

        onAdd() {
            this.isReady = true;
        }

        draw() {
            if (drawFunc) {
                drawFunc();
            }
        }

        onRemove() {
            this.isReady = false;
        }

        getProjectionSafe() {
            return this.isReady ? this.getProjection() : null;
        }

        getMapState(): {center: {lat: number; lng: number}; zoom: number} | null {
            const map = this.getMap() as google.maps.Map;
            if (!map) return null;
            const center = map.getCenter();
            const zoom = map.getZoom();
            if (!center || zoom === undefined) return null;
            return {
                center: {lat: center.lat(), lng: center.lng()},
                zoom
            };
        }
    }

    return new SimpleOverlay();
}

// ===== TYPES =====

type DrawingTool = "none" | "circle" | "polygon" | "line";

type Shape = {
    id: string;
    type: "circle";
    center: [number, number]; // [lng, lat]
    radiusKm: number;
    color: string;
};

// ===== MAIN COMPONENT =====

export function MercatorDrawPage() {
    const mapRef = useRef<google.maps.Map | null>(null);
    const mapDivRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const overlayRef = useRef<any>(null);
    const drawFuncRef = useRef<(() => void) | null>(null);

    const [shapes, setShapes] = useState<Shape[]>([]);
    const [activeTool, setActiveTool] = useState<DrawingTool>("none");
    const [selectedColor, setSelectedColor] = useState("#3B82F6");
    const [currentDrawing, setCurrentDrawing] = useState<Array<[number, number]>>([]);
    const [mousePos, setMousePos] = useState<[number, number] | null>(null);

    // Load Google Maps
    const {isLoaded, loadError} = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY || "",
    });

    // Hex to RGB
    const hexToRgb = useCallback((hex: string): [number, number, number] => {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        return [r, g, b];
    }, []);

    // Draw function
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        const map = mapRef.current;
        const overlay = overlayRef.current;

        if (!canvas || !map || !overlay) return;

        const mapState = overlay.getMapState?.();
        if (!mapState) return;

        const gl = canvas.getContext("webgl", {alpha: true, premultipliedAlpha: false});
        if (!gl) return;

        const mapDiv = map.getDiv();
        if (!mapDiv) return;

        const dpr = window.devicePixelRatio || 1;
        const width = mapDiv.offsetWidth;
        const height = mapDiv.offsetHeight;

        if (!width || !height) return;

        // Size canvas
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        canvas.width = width * dpr;
        canvas.height = height * dpr;

        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

        // Simple shader
        const vertexShaderSource = `
            attribute vec2 a_position;
            void main() {
                gl_Position = vec4(a_position, 0.0, 1.0);
            }
        `;

        const fragmentShaderSource = `
            precision mediump float;
            uniform vec4 u_color;
            void main() {
                gl_FragColor = u_color;
            }
        `;

        const compileShader = (source: string, type: number) => {
            const shader = gl.createShader(type);
            if (!shader) return null;
            gl.shaderSource(shader, source);
            gl.compileShader(shader);
            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                console.error("Shader error:", gl.getShaderInfoLog(shader));
                return null;
            }
            return shader;
        };

        const vs = compileShader(vertexShaderSource, gl.VERTEX_SHADER);
        const fs = compileShader(fragmentShaderSource, gl.FRAGMENT_SHADER);
        if (!vs || !fs) return;

        const program = gl.createProgram();
        if (!program) return;
        gl.attachShader(program, vs);
        gl.attachShader(program, fs);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.error("Program error:", gl.getProgramInfoLog(program));
            return;
        }

        gl.useProgram(program);

        const posLoc = gl.getAttribLocation(program, "a_position");
        const colorLoc = gl.getUniformLocation(program, "u_color");

        // Render circles
        for (const shape of shapes) {
            if (shape.type !== "circle") continue;

            const radiusMeters = shape.radiusKm * 1000;
            const [r, g, b] = hexToRgb(shape.color);

            // Adaptive segment count based on latitude (more segments at higher latitudes due to Mercator distortion)
            const centerLat = Math.abs(shape.center[1]);
            const baseSegments = 64;
            const latitudeFactor = Math.max(1, Math.cos(centerLat * Math.PI / 180) ** -1.5); // Increase segments as we approach poles
            const segments = Math.floor(baseSegments * latitudeFactor);

            // Generate circle points
            const centerNDC = latLngToNDC(
                shape.center[1],
                shape.center[0],
                mapState.center,
                mapState.zoom,
                width,
                height
            );

            if (!centerNDC) continue;

            const positions: number[] = [centerNDC.x, centerNDC.y];
            let validPoints = 0;
            let totalPoints = 0;

            for (let i = 0; i <= segments; i++) {
                totalPoints++;
                const bearing = (i / segments) * 360;
                const edgePoint = destinationPoint(
                    shape.center[1],
                    shape.center[0],
                    bearing,
                    radiusMeters
                );

                const edgeNDC = latLngToNDC(
                    edgePoint.lat,
                    edgePoint.lng,
                    mapState.center,
                    mapState.zoom,
                    width,
                    height
                );

                if (edgeNDC) {
                    positions.push(edgeNDC.x, edgeNDC.y);
                    validPoints++;
                }
            }

            console.log(`Circle at [${shape.center[0]}, ${shape.center[1]}]: ${validPoints}/${totalPoints} points valid, ${segments} segments, ${positions.length/2} total vertices`);

            if (positions.length >= 6) {
                const buffer = gl.createBuffer();
                gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
                gl.enableVertexAttribArray(posLoc);
                gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                // Draw filled circle
                gl.uniform4f(colorLoc, r, g, b, 0.5);
                gl.drawArrays(gl.TRIANGLE_FAN, 0, positions.length / 2);

                // Draw outline for debugging
                gl.uniform4f(colorLoc, r, g, b, 1.0);
                gl.drawArrays(gl.LINE_LOOP, 1, positions.length / 2 - 1); // Skip center point, draw edge loop
            }
        }

        // Draw preview
        if (activeTool === "circle" && currentDrawing.length === 1 && mousePos) {
            const radiusKm = haversineDistance(
                currentDrawing[0][1], currentDrawing[0][0],
                mousePos[1], mousePos[0]
            );
            const radiusMeters = radiusKm * 1000;
            const [r, g, b] = hexToRgb(selectedColor);

            // Adaptive segment count for preview (same logic as above)
            const centerLat = Math.abs(currentDrawing[0][1]);
            const baseSegments = 64;
            const latitudeFactor = Math.max(1, Math.cos(centerLat * Math.PI / 180) ** -1.5);
            const segments = Math.floor(baseSegments * latitudeFactor);

            const centerNDC = latLngToNDC(
                currentDrawing[0][1],
                currentDrawing[0][0],
                mapState.center,
                mapState.zoom,
                width,
                height
            );

            if (centerNDC) {
                const positions: number[] = [centerNDC.x, centerNDC.y];

                for (let i = 0; i <= segments; i++) {
                    const bearing = (i / segments) * 360;
                    const edgePoint = destinationPoint(
                        currentDrawing[0][1],
                        currentDrawing[0][0],
                        bearing,
                        radiusMeters
                    );

                    const edgeNDC = latLngToNDC(
                        edgePoint.lat,
                        edgePoint.lng,
                        mapState.center,
                        mapState.zoom,
                        width,
                        height
                    );

                    if (edgeNDC) {
                        positions.push(edgeNDC.x, edgeNDC.y);
                    }
                }

                if (positions.length >= 6) {
                    const buffer = gl.createBuffer();
                    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
                    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
                    gl.enableVertexAttribArray(posLoc);
                    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                    gl.uniform4f(colorLoc, r, g, b, 0.25);
                    gl.drawArrays(gl.TRIANGLE_FAN, 0, positions.length / 2);
                }
            }
        }
    }, [shapes, currentDrawing, mousePos, activeTool, selectedColor, hexToRgb]);

    // Keep draw func ref in sync
    useEffect(() => {
        drawFuncRef.current = draw;
    }, [draw]);

    // Initialize map and canvas
    useEffect(() => {
        if (!isLoaded || !mapDivRef.current) return;

        const map = new google.maps.Map(mapDivRef.current, {
            center: {lat: 20, lng: 0},
            zoom: 2,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
        });

        mapRef.current = map;

        // Create canvas
        const canvas = document.createElement("canvas");
        canvas.style.position = "absolute";
        canvas.style.left = "0";
        canvas.style.top = "0";
        canvas.style.pointerEvents = "none";
        canvasRef.current = canvas;

        const mapDiv = map.getDiv();
        mapDiv.appendChild(canvas);

        // Create overlay
        const overlay = createOverlay(() => {
            if (drawFuncRef.current) {
                drawFuncRef.current();
            }
        });
        overlay.setMap(map);
        overlayRef.current = overlay;

        setTimeout(() => {
            if (drawFuncRef.current) {
                drawFuncRef.current();
            }
        }, 100);

        return () => {
            overlay.setMap(null);
            if (canvasRef.current) {
                canvasRef.current.remove();
            }
            overlayRef.current = null;
            mapRef.current = null;
        };
    }, [isLoaded]);

    // Redraw when shapes change
    useEffect(() => {
        if (overlayRef.current && mapRef.current) {
            draw();
        }
    }, [shapes, currentDrawing, draw]);

    // Handle map clicks
    const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (!e.latLng || activeTool === "none") return;

        const lat = e.latLng.lat();
        const lng = e.latLng.lng();

        if (activeTool === "circle") {
            if (currentDrawing.length === 0) {
                setCurrentDrawing([[lng, lat]]);
            } else {
                const radiusKm = haversineDistance(
                    currentDrawing[0][1], currentDrawing[0][0],
                    lat, lng
                );
                setShapes(prev => [...prev, {
                    id: Math.random().toString(36).substr(2, 9),
                    type: "circle",
                    center: currentDrawing[0],
                    radiusKm,
                    color: selectedColor
                }]);
                setCurrentDrawing([]);
                setActiveTool("none");
            }
        }
    }, [activeTool, currentDrawing, selectedColor]);

    // Handle mouse move
    const handleMouseMove = useCallback((e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        setMousePos([e.latLng.lng(), e.latLng.lat()]);
    }, []);

    // Attach event listeners
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const clickListener = map.addListener("click", handleMapClick);
        const moveListener = map.addListener("mousemove", handleMouseMove);

        return () => {
            google.maps.event.removeListener(clickListener);
            google.maps.event.removeListener(moveListener);
        };
    }, [handleMapClick, handleMouseMove]);

    // Toggle map interactions
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        if (activeTool !== "none") {
            map.setOptions({
                draggable: false,
                zoomControl: false,
                scrollwheel: false,
                disableDoubleClickZoom: true,
            });
        } else {
            map.setOptions({
                draggable: true,
                zoomControl: true,
                scrollwheel: true,
                disableDoubleClickZoom: false,
            });
        }
    }, [activeTool]);

    if (loadError) return <div>Error loading maps</div>;
    if (!isLoaded) return <div>Loading maps...</div>;

    return (
        <div className="h-screen flex flex-col">
            <div className="flex-1 flex">
                {/* Sidebar */}
                <div className="w-64 bg-gray-100 p-4 overflow-y-auto">
                    <h2 className="text-xl font-bold mb-4">Mercator Draw (Pure Projection)</h2>

                    {/* Tools */}
                    <div className="mb-4">
                        <h3 className="font-semibold mb-2">Tools</h3>
                        <div className="space-y-2">
                            <button
                                onClick={() => setActiveTool("circle")}
                                className={`w-full px-4 py-2 rounded ${activeTool === "circle" ? "bg-blue-600 text-white" : "bg-white"}`}
                            >
                                Circle
                            </button>
                        </div>
                    </div>

                    {/* Color */}
                    <div className="mb-4">
                        <h3 className="font-semibold mb-2">Color</h3>
                        <input
                            type="color"
                            value={selectedColor}
                            onChange={(e) => setSelectedColor(e.target.value)}
                            className="w-full h-10"
                        />
                    </div>

                    {/* Test Buttons */}
                    <div className="mb-4">
                        <h3 className="font-semibold mb-2">Test Circles</h3>
                        <div className="space-y-2">
                            <button
                                onClick={() => setShapes(prev => [...prev, {
                                    id: Math.random().toString(36).substr(2, 9),
                                    type: "circle",
                                    center: [0, 0],
                                    radiusKm: 2000,
                                    color: "#3B82F6"
                                }])}
                                className="w-full px-4 py-2 rounded bg-white text-sm"
                            >
                                Equator Large (2000km)
                            </button>
                            <button
                                onClick={() => setShapes(prev => [...prev, {
                                    id: Math.random().toString(36).substr(2, 9),
                                    type: "circle",
                                    center: [0, 80],
                                    radiusKm: 2000,
                                    color: "#10B981"
                                }])}
                                className="w-full px-4 py-2 rounded bg-white text-sm"
                            >
                                North Pole (80°, 2000km)
                            </button>
                            <button
                                onClick={() => setShapes(prev => [...prev, {
                                    id: Math.random().toString(36).substr(2, 9),
                                    type: "circle",
                                    center: [0, -80],
                                    radiusKm: 2000,
                                    color: "#EF4444"
                                }])}
                                className="w-full px-4 py-2 rounded bg-white text-sm"
                            >
                                South Pole (-80°, 2000km)
                            </button>
                            <button
                                onClick={() => setShapes(prev => [...prev, {
                                    id: Math.random().toString(36).substr(2, 9),
                                    type: "circle",
                                    center: [170, 0],
                                    radiusKm: 2000,
                                    color: "#F59E0B"
                                }])}
                                className="w-full px-4 py-2 rounded bg-white text-sm"
                            >
                                Date Line (170°, 2000km)
                            </button>
                        </div>
                    </div>

                    {/* Shapes List */}
                    <div>
                        <h3 className="font-semibold mb-2">Shapes ({shapes.length})</h3>
                        <ul className="space-y-1">
                            {shapes.map(shape => (
                                <li key={shape.id} className="flex items-center justify-between bg-white p-2 rounded text-sm">
                                    <span>Circle {shape.radiusKm.toFixed(0)}km</span>
                                    <button
                                        onClick={() => setShapes(prev => prev.filter(s => s.id !== shape.id))}
                                        className="text-red-600 hover:text-red-800"
                                    >
                                        ×
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Map */}
                <div className="flex-1 relative">
                    <div
                        ref={mapDivRef}
                        className="w-full h-full"
                        style={{cursor: activeTool !== "none" ? "crosshair" : "default"}}
                    />
                </div>
            </div>
        </div>
    );
}
